// A set of function for accessing type index entries.

import { SolidContainerUri, SolidLeafUri } from "@ldo/connected-solid";
import { useLdo } from "@ldo/solid-react";
import { SolidProfile } from "../.ldo/solidProfile.typings";
import { TypeRegistrationShapeType } from "../.ldo/typeIndex.shapeTypes";
import { TypeRegistration } from "../.ldo/typeIndex.typings";
import { solid } from "./ns";
import { useEnsureAllLoaded } from "./useAllResources";

// List all registered instances of the given type.
//
// Will return undefined if `profile` is undefined or the type indexes are nor loaded yet.
export function useAllRegisteredInstances(profile: SolidProfile | undefined, typeUri: string): SolidLeafUri[] | undefined {
  return useAllRegistrations(profile, typeUri)
    ?.flatMap(typereg => typereg.instance?.toArray() || [])
    .map(x => x['@id'] as SolidLeafUri);
}

// List all containers that are registered as containing instances of the given type.
//
// Will return undefined if `profile` is undefined or the type indexes are nor loaded yet.
export function useAllRegisteredContainers(profile: SolidProfile | undefined, typeUri: string): SolidContainerUri[] | undefined {
  return useAllRegistrations(profile, typeUri)
    ?.flatMap(typereg => typereg.instanceContainer?.toArray() || [])
    .map(x => x['@id'] as SolidContainerUri);
}

// List all registrations for the given type.
//
// Will return undefined if `profile` is undefined or the type indexes are nor loaded yet.
export function useAllRegistrations(profile: SolidProfile | undefined, typeUri: string): TypeRegistration[] | undefined {
  const { dataset } = useLdo();
  if (!useAllTypeIndexes(profile)) return undefined;
  return dataset
    .usingType(TypeRegistrationShapeType)
    .matchSubject(solid.forClass, typeUri)
    .toArray()
}

// Return true when all type indexes (public and private) listed in `profile` have been loaded.
export function useAllTypeIndexes(profile?: SolidProfile): boolean {
  const allTypeIndexes = [
    ...(profile?.privateTypeIndex || []),
    ...(profile?.publicTypeIndex || []),
  ].map(obj => obj['@id']);
  const allLoaded = useEnsureAllLoaded(allTypeIndexes);

  return profile !== undefined && allLoaded;
}
